import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Download,
  FileText,
  Filter,
  HelpCircle,
  LayoutGrid,
  LayoutList,
  Lock,
  MessageSquare,
  Mail,
  Plus,
  QrCode,
  RefreshCw,
  Save,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { TooltipIconButton } from '../ui/tooltip-icon-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

function HelpPill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)] px-2 py-1 text-[11px] font-semibold text-[var(--ba-body-text)]">
      {Icon && <Icon className="h-3.5 w-3.5 text-[var(--ba-muted)]" />}
      {children}
    </span>
  );
}

function HelpCta({ icon: Icon, label, variant = 'outline' }) {
  return (
    <Button
      type="button"
      variant={variant}
      className="h-8 gap-2 rounded-lg px-3 text-xs font-semibold uppercase tracking-wide"
      onClick={() => {}}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </Button>
  );
}

function PageHeader({ title, description, backTo = '/', backLabel = 'Torna alla home' }) {
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);

  const help = useMemo(() => {
    const path = location.pathname;

    if (path.startsWith('/editor-invito')) {
      return {
        title: 'Guida — Creazione invito',
        description: 'Crea un nuovo invito e pubblicalo con un link condivisibile.',
        steps: [
          { k: 'Template', v: 'Scegli un template preconfigurato oppure «Nessuno» per partire da zero con un invito personalizzato.', icon: LayoutGrid },
          { k: 'Info', v: 'Imposta nome e descrizione: determinano anche il link (/invito?id=…).' },
          { k: 'Aspetto', v: 'Personalizza colori, logo, immagine di copertina e sfondo opzionale. Senza sfondo l\'invito pubblico usa sempre il tema chiaro.' },
          {
            k: 'Campi',
            v: 'Aggiungi le domande del modulo (testo, email, scelta singola/multipla, consenso, ecc.) e riordinali con le frecce. Ogni campo può essere obbligatorio o facoltativo.',
          },
          {
            k: 'Sezioni',
            v: 'Usa «Aggiungi sezione» per raggruppare campi correlati sotto un titolo (es. «Dettagli volontario»). I campi dentro una sezione compaiono solo quando la sezione è visibile.',
          },
          {
            k: 'Visibilità condizionale',
            v: 'Apri «Visibilità» su un campo o sezione per mostrarlo solo se un altro campo — posizionato sopra nel modulo — soddisfa una condizione (es. «Partecipi come» è uguale a «Volontario»). Usa l\'anteprima logica nel tab Campi per verificare il risultato prima di pubblicare.',
          },
          { k: 'Impostazioni', v: 'Apri/chiudi iscrizioni e data chiusura (se serve).', icon: Lock },
        ],
      };
    }

    if (path.startsWith('/tutti-gli-inviti')) {
      return {
        title: 'Guida — Lista inviti',
        description: 'Gestisci inviti esistenti: risposte, condivisione e stato iscrizioni.',
        steps: [
          {
            k: 'Tabella / Card',
            v: 'Scegli la vista migliore per il tuo lavoro.',
            ctas: [
              { icon: LayoutList, label: 'Tabella', variant: 'outline' },
              { icon: LayoutGrid, label: 'Card', variant: 'outline' },
            ],
          },
          {
            k: 'Condividi',
            v: 'Apri il pannello per copiare il link e scaricare il QR code.',
            ctas: [{ icon: QrCode, label: 'Condividi', variant: 'outline' }],
          },
          {
            k: 'Risposte',
            v: 'Apri l’elenco risposte per consultare, filtrare ed esportare.',
            ctas: [{ icon: MessageSquare, label: 'Risposte', variant: 'outline' }],
          },
          {
            k: 'Statistiche',
            v: 'Apri i grafici e le distribuzioni (solo se ci sono risposte).',
            ctas: [{ icon: BarChart3, label: 'Statistiche', variant: 'outline' }],
          },
          {
            k: 'Apri/chiudi',
            v: 'Gestisci lo stato iscrizioni senza cambiare il modulo.',
            ctas: [{ icon: Lock, label: 'Apri/chiudi', variant: 'outline' }],
          },
        ],
      };
    }

    if (path === '/eventi/nuovo') {
      return {
        title: 'Guida — Creazione evento',
        description: 'Crea un evento collegato a un invito per organizzare i turni.',
        steps: [
          { k: 'Sorgente', v: 'Scegli l’invito da cui leggere le risposte.', pills: [{ icon: MessageSquare, label: 'Risposte invito' }] },
          { k: 'Campi', v: 'Associa i campi (nome, turno, disponibilità) se richiesto.' },
          { k: 'Planner', v: 'Dopo la creazione puoi trascinare i partecipanti nei negozi/turni.', ctas: [{ icon: LayoutGrid, label: 'Apri planner', variant: 'default' }] },
        ],
      };
    }

    if (path.startsWith('/eventi/')) {
      return {
        title: 'Guida — Planner evento',
        description: 'Organizza i turni e sincronizza le nuove risposte.',
        steps: [
          { k: 'Sincronizza', v: 'Importa nuove risposte dall’invito collegato.', ctas: [{ icon: RefreshCw, label: 'Sincronizza', variant: 'outline' }] },
          { k: 'Drag & drop', v: 'Sposta i partecipanti nei negozi e nei turni.' },
          { k: 'Esporta', v: 'Scarica il piano in Excel o PDF.', ctas: [{ icon: Download, label: 'Excel', variant: 'outline' }, { icon: FileText, label: 'PDF', variant: 'outline' }] },
          { k: 'Salva', v: 'Salva le modifiche per non perdere le assegnazioni.', ctas: [{ icon: Save, label: 'Salva', variant: 'default' }] },
        ],
      };
    }

    if (path === '/eventi') {
      return {
        title: 'Guida — Lista eventi',
        description: 'Apri un planner o gestisci gli eventi creati.',
        steps: [
          { k: 'Apri planner', v: 'Entra nell’evento per assegnazioni e turni.', ctas: [{ icon: LayoutGrid, label: 'Apri planner', variant: 'default' }] },
          { k: 'Nuovo evento', v: 'Crea un evento collegandolo a un invito.', ctas: [{ icon: Plus, label: 'Nuovo evento', variant: 'default' }] },
          { k: 'Elimina', v: 'Rimuove l’evento (non cancella le risposte dell’invito).', pills: [{ icon: null, label: 'Elimina' }] },
        ],
      };
    }

    if (path.startsWith('/risposte/statistiche')) {
      return {
        title: 'Guida — Statistiche risposte',
        description: 'Analisi visiva delle risposte: trend, distribuzioni e copertura.',
        steps: [
          { k: 'Panoramica', v: 'Totali, media giornaliera, prime/ultime iscrizioni.' },
          { k: 'Grafici', v: 'Andamento nel tempo, giorno settimana e fascia oraria.' },
          { k: 'Per campo', v: 'Distribuzione per domanda (scelte, numeri, consensi, date).' },
        ],
      };
    }

    if (path.startsWith('/risposte')) {
      return {
        title: 'Guida — Elenco risposte',
        description: 'Filtra, seleziona e esporta le risposte ricevute.',
        steps: [
          { k: 'Filtri', v: 'Cerca per testo o per campo e combina più filtri.', pills: [{ icon: Filter, label: 'Filtri' }] },
          { k: 'Selezione', v: 'Clicca le righe per selezionare ed esportare solo alcune risposte.' },
          { k: 'Export', v: 'Scarica Excel completo o solo selezionate.', ctas: [{ icon: Download, label: 'Esporta', variant: 'outline' }] },
          { k: 'Promemoria', v: 'Apri il wizard per scegliere il campo email e aprire il client di posta con tutti i partecipanti in BCC.', ctas: [{ icon: Mail, label: 'Promemoria email', variant: 'outline' }] },
          { k: 'Statistiche', v: 'Passa al tab Statistiche per grafici e insight.', ctas: [{ icon: BarChart3, label: 'Statistiche', variant: 'outline' }] },
        ],
      };
    }

    return null;
  }, [location.pathname]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="mb-8"
    >
      <Link
        to={backTo}
        title={backLabel}
        className="mb-4 flex w-fit items-center gap-1.5 text-sm text-[var(--ba-muted)] transition-colors hover:text-brand-orange"
      >
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </Link>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold text-[var(--ba-body-text)] md:text-3xl">{title}</h1>
          {help && (
            <TooltipIconButton
              label="Guida"
              icon={HelpCircle}
              variant="outline"
              size="icon"
              className="h-9 w-9"
              iconClassName="h-4 w-4"
              onClick={() => setHelpOpen(true)}
            />
          )}
        </div>
        <span className="h-1 w-16 rounded-full bg-brand-orange" />
      </div>
      {description && <p className="mt-2 max-w-2xl ba-muted">{description}</p>}

      {help && (
        <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{help.title}</DialogTitle>
              <DialogDescription>{help.description}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <ul className="space-y-3">
                {help.steps.map((step) => (
                  <li key={step.k} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand-orange" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="font-bold text-[var(--ba-body-text)]">{step.k}</p>
                        {(step.ctas?.length > 0 || step.pills?.length > 0) && (
                          <div className="flex flex-wrap gap-2">
                            {step.ctas?.map((cta, index) => (
                              <HelpCta key={`${step.k}-cta-${index}`} icon={cta.icon} label={cta.label} variant={cta.variant} />
                            ))}
                            {step.pills?.map((pill, index) => (
                              <HelpPill key={`${step.k}-pill-${index}`} icon={pill.icon}>
                                {pill.label}
                              </HelpPill>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-sm ba-muted">{step.v}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setHelpOpen(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </motion.div>
  );
}

export default PageHeader;
